import React, {
  createContext,
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import Fuse from "fuse.js";
import { useLocation } from "react-router-dom";
import { Box, HStack, VStack } from "../../components/utils";
import BCHeader from "../../shared/BCHeader";
import ESignSettings from "./ESignSettings";
import EmailSettings from "./EmailSettings";
import EmailTemplateSettings from "./EmailTemplateSettings";
// Vendored editor exports a default settings page.
// eslint-disable-next-line import/no-named-as-default, import/no-named-as-default-member
import EmailTemplateEditor from "./EmailTemplateEditor";
import EmployeePortalSettings from "./EmployeePortalSettings";
import SecuritySettings from "./SecuritySettings";
import CCEmailSettings from "./CCEmailSettings";
import GrantLetterSettings from "./GrantLetterSettings";
import GrantLetterTemplateSettings from "./GrantLetterTemplateSettings";
import EmployeeDatasourceSettings from "./EmployeeDatasourceSettings";
import ReportSettings from "./ReportSettings";
import { useGetAllCompanyConfigs } from "../../queries";
import SearchInput from "../../components/shared/SearchInput";
import metadata from "./settings-metadata.json";
import AlertDialog from "../../components/shared/AlertDialog";
import {
  useNavigationBlocker,
  NavBlockerTx,
} from "../../utils/useNavigationBlocker";

export const SiteSettingsContext = createContext(false);
function SiteSettings() {
  const location = useLocation();
  const [activeSetting, setActiveSetting] = useState<string>(
    (location.state as any)?.activeSetting || "Email Settings"
  );
  const [emailTemplateEditorTemplateId, setEmailTemplateEditorTemplateId] =
    useState("");
  const [isGrantTemplateDirty, setIsGrantTemplateDirty] = useState(false);
  const isGrantTemplateDirtyRef = useRef(false);
  const [pendingActiveSetting, setPendingActiveSetting] = useState<
    string | null
  >(null);
  const [pendingNavTx, setPendingNavTx] = useState<NavBlockerTx | null>(null);

  // Block in-app router navigation when dirty
  const routerBlocker = useCallback((tx: NavBlockerTx) => {
    setPendingNavTx(tx);
  }, []);
  useNavigationBlocker(
    routerBlocker,
    isGrantTemplateDirty && activeSetting === "Grant Template Settings"
  );

  const [searchTerm, setSearchTerm] = useState<string>("");
  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [debouncedTerm, setDebouncedTerm] = useState("");
  const [activeTabForChild, setActiveTabForChild] = useState<{
    settingType?: string;
    category?: string;
  }>({});

  function highlightMatches(text: string, search: string) {
    if (!search) return text;

    const terms = search
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")); // escape regex

    if (terms.length === 0) return text;

    const regex = new RegExp(`(${terms.join("|")})`, "gi");
    const parts = text.split(regex);

    return parts.map((part, i) =>
      regex.test(part) ? (
        <span key={i} className="bg-orange-200">
          {part}
        </span>
      ) : (
        part
      )
    );
  }
  const { data: configs } = useGetAllCompanyConfigs();

  useEffect(() => {
    if (!showDropdown) setSearchTerm("");
  }, [showDropdown]);

  useEffect(() => {
    const checkIfClickOutside = (e: any) => {
      if (!(showDropdown && inputRef.current?.contains(e.target))) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", checkIfClickOutside);
    return () => {
      document.removeEventListener("mousedown", checkIfClickOutside);
    };
  }, [showDropdown]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedTerm(searchTerm.trim().toLowerCase());
    }, 300);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  const fuse = useMemo(
    () =>
      new Fuse(metadata, {
        keys: ["label", "description"],
        threshold: 0.3,
        ignoreLocation: true,
      }),
    [metadata]
  );

  const filtered = useMemo(() => {
    if (!debouncedTerm) return metadata;

    // split the input into separate words
    const terms = debouncedTerm.split(" ").filter(Boolean);

    // require that every term matches somewhere in the item
    return metadata.filter((item: any) =>
      terms.every((term) =>
        fuse.search(term).some((result) => result.item === item)
      )
    );
  }, [debouncedTerm, fuse, metadata]);

  function requestActiveSetting(next: string) {
    if (
      activeSetting === "Grant Template Settings" &&
      isGrantTemplateDirtyRef.current
    ) {
      setPendingActiveSetting(next);
    } else {
      setActiveSetting(next);
    }
  }

  function handleSelectItem(item: any) {
    const target = item.settingType || item.settingType;
    if (target) requestActiveSetting(target);
    setActiveTabForChild({
      settingType: item.settingType,
      category: item.category,
    });
    setShowDropdown(false);

    const targetId =
      item.id ||
      `${(item.settingType || "").toLowerCase().replace(/\s+/g, "-")}-${
        item.name
      }`;
    setTimeout(() => {
      const el = document.getElementById(targetId);
      if (el) {
        try {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        } catch (e) {
          // do nothing
        }
        const highlightClasses = [
          "ring",
          "ring-orange-500",
          "rounded-md",
          "bg-orange-50",
          "px-4",
        ];
        el.classList.add(...highlightClasses);
        try {
          window.history.replaceState(null, "", `#${targetId}`);
        } catch (e) {
          // do nothing
        }
        const removeHighlight = () => {
          el.classList.remove(...highlightClasses);
          el.removeEventListener("click", removeHighlight);
        };
        el.addEventListener("click", removeHighlight);
        setTimeout(removeHighlight, 1200);
      }
    }, 200);
  }
  return (
    <>
      <VStack className="gap-4">
        <div className="flex items-baseline justify-between">
          <BCHeader
            className="items-baseline "
            bcTitle="Settings"
            bcSubTitle="Site Settings"
          />
        </div>
        <Box className="my-auto rounded-lg drop-shadow-md">
          <HStack className="w-full gap-4">
            <Box className="h-full min-h-full bg-white rounded-lg w-21 min-w-min">
              <Box className="px-4 mt-4 flex flex-col gap-2 text-lg font-medium text-gray-dark whitespace-nowrap relative">
                <div className="w-full" ref={inputRef}>
                  <SearchInput
                    placeholder="Search Settings"
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setShowDropdown(true);
                    }}
                    onFocus={() => setShowDropdown(true)}
                  />
                  {showDropdown && (
                    <div className="absolute z-50 bg-white border mt-2 rounded shadow-lg max-h-64 overflow-auto">
                      {filtered.length === 0 ? (
                        <div className="p-3 text-sm text-gray-500">
                          No results found
                        </div>
                      ) : (
                        filtered.map((item: any) => (
                          <div
                            key={item.id}
                            onClick={() => handleSelectItem(item)}
                            className="p-3 hover:bg-gray-50 cursor-pointer"
                          >
                            <div className="font-medium text-sm">
                              {highlightMatches(
                                item.label || "",
                                debouncedTerm
                              )}
                            </div>
                            <div className="text-xxs text-gray-400">
                              {item.settingType} — {item.category}
                            </div>
                            <div className="text-xxs text-gray-500 truncate">
                              {highlightMatches(
                                item.description || "",
                                debouncedTerm
                              )}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
                Site Settings Menu
              </Box>
              <VStack className="justify-between h-[500px]">
                <Box
                  className={`cursor-pointer  form-label text-sm font-medium   p-4  whitespace-nowrap ${
                    activeSetting === "Email Settings"
                      ? "rounded text-orange-501"
                      : ""
                  }`}
                  onClick={() => requestActiveSetting("Email Settings")}
                >
                  Email Settings
                </Box>
                <Box
                  className={`cursor-pointer  form-label text-sm font-medium   p-4  whitespace-nowrap ${
                    activeSetting === "Email CC Settings"
                      ? "rounded text-orange-501"
                      : ""
                  }`}
                  onClick={() => requestActiveSetting("Email CC Settings")}
                >
                  Email CC Settings
                </Box>
                <Box
                  className={`cursor-pointer form-label text-sm font-medium p-4 whitespace-nowrap ${
                    activeSetting === "Report Settings"
                      ? "rounded text-orange-501"
                      : ""
                  } hidden`}
                  onClick={() => requestActiveSetting("Report Settings")}
                >
                  Report Settings
                </Box>
                <Box
                  className={`cursor-pointer  form-label text-sm font-medium   p-4  whitespace-nowrap ${
                    activeSetting === "Employee Portal Settings"
                      ? "rounded text-orange-501"
                      : ""
                  }`}
                  onClick={() =>
                    requestActiveSetting("Employee Portal Settings")
                  }
                >
                  Employee Portal Settings
                </Box>
                <Box
                  className={`cursor-pointer  form-label text-sm font-medium   p-4  whitespace-nowrap ${
                    activeSetting === "Grant Settings"
                      ? "rounded text-orange-501"
                      : ""
                  }`}
                  onClick={() => requestActiveSetting("Grant Settings")}
                >
                  Grant Settings
                </Box>
                <Box
                  className={`cursor-pointer  form-label text-sm font-medium   p-4  whitespace-nowrap ${
                    activeSetting === "ESign Settings"
                      ? "rounded text-orange-501"
                      : ""
                  }`}
                  onClick={() => requestActiveSetting("ESign Settings")}
                >
                  E-Sign Settings
                </Box>
                {(configs || []).length > 0 && (
                  <Box
                    className={`cursor-pointer  form-label text-sm font-medium  p-4  whitespace-nowrap ${
                      activeSetting === "Employee Datasource Settings"
                        ? "rounded text-orange-501"
                        : ""
                    }`}
                    onClick={() =>
                      requestActiveSetting("Employee Datasource Settings")
                    }
                  >
                    Employee Datasource Settings
                  </Box>
                )}
                <Box
                  className={`cursor-pointer  form-label text-sm font-medium   p-4  whitespace-nowrap ${
                    activeSetting === "Email Template Settings" ||
                    activeSetting === "Email Template Editor"
                      ? "rounded text-orange-501"
                      : ""
                  }`}
                  onClick={() =>
                    requestActiveSetting("Email Template Settings")
                  }
                >
                  Email Template Settings
                </Box>
                <Box
                  className={`cursor-pointer  form-label text-sm font-medium   p-4  whitespace-nowrap ${
                    activeSetting === "Grant Template Settings"
                      ? "rounded text-orange-501"
                      : ""
                  }`}
                  onClick={() =>
                    requestActiveSetting("Grant Template Settings")
                  }
                >
                  Grant Template Settings
                </Box>
              </VStack>
            </Box>
            <Box className="flex bg-white w-full">
              {activeSetting === "Email Settings" && (
                <EmailSettings openCategory={activeTabForChild.category} />
              )}
              {activeSetting === "Email CC Settings" && <CCEmailSettings />}
              {activeSetting === "Employee Portal Settings" && (
                <EmployeePortalSettings
                  openCategory={activeTabForChild.category}
                />
              )}
              {activeSetting === "Employee Datasource Settings" && (
                <EmployeeDatasourceSettings />
              )}
              {activeSetting === "Report Settings" && (
                <ReportSettings openCategory={activeTabForChild.category} />
              )}
              {activeSetting === "Grant Settings" && (
                <GrantLetterSettings
                  openCategory={activeTabForChild.category}
                />
              )}
              {activeSetting === "ESign Settings" && <ESignSettings />}
              {activeSetting === "Security Settings" && <SecuritySettings />}
              {activeSetting === "Email Template Settings" && (
                <EmailTemplateSettings
                  onOpenSidebarEditor={(templateId) => {
                    setEmailTemplateEditorTemplateId(templateId);
                    requestActiveSetting("Email Template Editor");
                  }}
                />
              )}
              {activeSetting === "Email Template Editor" && (
                <EmailTemplateEditor
                  initialTemplateId={emailTemplateEditorTemplateId}
                  onViewAll={() => {
                    setEmailTemplateEditorTemplateId("");
                    requestActiveSetting("Email Template Settings");
                  }}
                />
              )}
              {activeSetting === "Grant Template Settings" && (
                <GrantLetterTemplateSettings
                  onDirtyChange={(dirty) => {
                    isGrantTemplateDirtyRef.current = dirty;
                    setIsGrantTemplateDirty(dirty);
                  }}
                />
              )}
            </Box>
          </HStack>
        </Box>
      </VStack>
      <AlertDialog
        open={!!pendingActiveSetting}
        onClose={() => setPendingActiveSetting(null)}
        message="You have unsaved changes in Grant Template Settings. Are you sure you want to leave?"
        primaryActionText="Leave"
        secondaryActionText="Stay"
        onPrimaryAction={() => {
          if (pendingActiveSetting) setActiveSetting(pendingActiveSetting);
          setPendingActiveSetting(null);
          isGrantTemplateDirtyRef.current = false;
          setIsGrantTemplateDirty(false);
        }}
        onSecondaryAction={() => setPendingActiveSetting(null)}
      />
      <AlertDialog
        open={!!pendingNavTx}
        onClose={() => setPendingNavTx(null)}
        message="You have unsaved changes in Grant Template Settings. Are you sure you want to leave?"
        primaryActionText="Leave"
        secondaryActionText="Stay"
        onPrimaryAction={() => {
          isGrantTemplateDirtyRef.current = false;
          setIsGrantTemplateDirty(false);
          pendingNavTx?.retry();
          setPendingNavTx(null);
        }}
        onSecondaryAction={() => setPendingNavTx(null)}
      />
    </>
    //     </Form>
    //   )}
    // </Formik>
  );
}

export default SiteSettings;
